import clsx from "clsx";
import _ from "lodash";
import { Plus, Target, OctagonAlert } from "lucide-react";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import {
  CustomDestinationOption,
  DestinationSelect,
} from "@/components/DestinationSelect";
import {
  allDestinationsData,
  DestinationDefinition,
  destinationKeyToSlugMap,
  SaaSDestination,
} from "@/destinations/destinations";
import {
  DestinationKey,
  DestinationName,
  DestinationSlug,
  DestinationStandardMapping,
  DestinationStandardMappingItemNamed,
  DestinationStandardMappingItemWildcard,
} from "@/destinations/types";
import { makeRequest } from "@/util";
import {
  LoadedEventDefinition as EventDefinition,
  LoadedEventDefinition,
  LoadedDestinationInfo,
} from "@/types/global";
import { TransformDefinitionSet } from "@/transforms/transformationDefinition";
import { TransformationIntention } from "@/transforms/types";
import { stringDistance } from "@/transforms/utils";
import { eventDefType } from "@/eventTypes";
import { useDestinationMetadataManager } from "@/hooks/useDestinationMetadataManager";
import useArrayOfDestinations from "@/hooks/useArrayOfDestinations";
import { themedSelectStyles } from "@/components/Dropdown/themedSelectStyles";

const selectStyles = themedSelectStyles();

const largeSelectStyles = themedSelectStyles<{
  label: React.ReactNode;
  value: string;
}>({
  container: (base) => ({
    ...base,
    display: "inline-block",
    height: 60,
    marginBottom: 10,
  }),
  menu: (base) => ({
    ...base,
    top: 60,
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: 60,
  }),
});

const sortedEventMappingForEvent = (
  fpEventName: string,
  destinationMappingItems: DestinationStandardMappingItemNamed[],
) => {
  return _.orderBy(
    destinationMappingItems,
    (item, index) => {
      const distance = stringDistance(fpEventName, item.eventName);
      if (distance < 10) {
        return distance;
      } else {
        return 10 + index;
      }
    },
    "asc",
  );
};
const WildcardCustomOption: React.FC<{
  wildcardConfig: DestinationStandardMappingItemWildcard | null;
  inputValue: string;
}> = ({ wildcardConfig, inputValue }) => (
  <>
    <div className="text-md text-textDefault">
      <Tooltip content="New standard mapping">
        <Plus className="mr-2" style={{ position: "relative", top: -2 }} />
      </Tooltip>
      {inputValue}
    </div>
    {wildcardConfig && (
      <div className="text-textDefault text-sm">
        {wildcardConfig.description}
      </div>
    )}
  </>
);

const getEnabledDestinationsForEventDef = (
  enabledDests: Record<string, boolean>,
  availableDestsIDToName: Record<string, DestinationKey>,
): Partial<Record<DestinationKey, boolean>> => {
  const result: Partial<Record<DestinationName, boolean>> = {};

  for (let enabledDestID in enabledDests) {
    let isEnabled = enabledDests[enabledDestID];
    let destName = availableDestsIDToName[enabledDestID];
    result[destName] = isEnabled;
  }

  return result;
};

export const ALL_DESTINATIONS_VALUE = "ALL_DESTINATIONS_VALUE";
export const ALL_EVENTS_VALUE = "ALL_EVENTS_VALUE";

const allDestinationOption: CustomDestinationOption = {
  value: ALL_DESTINATIONS_VALUE,
  icon: <></>,
  label: <em>All Integrations</em>,
};

const allEventsOption = {
  value: ALL_EVENTS_VALUE,
  label: (<em>All Events</em>) as unknown as string,
};

const SelectStandardEventWithWildcard: React.FC<{
  selectedDestinationDefinition: DestinationDefinition;
  selectedDestinationMappingEventOptions: {
    label: React.ReactNode;
    value: string;
  }[];
  setMappingEvent: (
    option: { label: React.ReactNode; value: string } | null,
  ) => void;
  selectedDestinationMappingEventSelectedOption:
    | { label: React.ReactNode; value: string }
    | null
    | undefined;
  selectedDestinationStandardMappingConfig: DestinationStandardMapping;
}> = ({
  selectedDestinationDefinition,
  selectedDestinationMappingEventOptions,
  setMappingEvent,
  selectedDestinationMappingEventSelectedOption,
  selectedDestinationStandardMappingConfig,
}) => {
  const wildcard: DestinationStandardMappingItemWildcard =
    selectedDestinationStandardMappingConfig.wildcard as DestinationStandardMappingItemWildcard;
  const mappingName =
    selectedDestinationDefinition instanceof SaaSDestination
      ? selectedDestinationDefinition.eventMappingEventName
      : "";
  const noOptionsMsg =
    `Start typing to add your own ${
      wildcard.description || mappingName
    }`.trim() + "...";

  const formatNewLabel = (inputValue: string) => (
    <>
      <div className="text-md text-textDefault">
        <Tooltip content="Add a new standard mapping">
          <Plus className="mr-2" style={{ position: "relative", top: -2 }} />
        </Tooltip>
        Add <code>{inputValue}</code> event
      </div>
      {wildcard.description && (
        <div className="text-textDefault text-sm">
          Add any additional event that you’ve defined for the purpose of
          audience targeting. Unique events aren’t available for conversion
          reporting. Keep in mind that whitespace will be trimmed from raw event
          names passed through the tag.
        </div>
      )}
    </>
  );

  const formatNewOption = (inputValue: string) => ({
    value: inputValue,
    label: (
      <WildcardCustomOption
        wildcardConfig={selectedDestinationStandardMappingConfig.wildcard}
        inputValue={inputValue}
      />
    ),
  });

  return (
    <CreatableSelect
      className="mt-4 mb-4 h-6 w-full"
      placeholder={`${selectedDestinationDefinition.name} Standard Mappings...`}
      options={selectedDestinationMappingEventOptions}
      onChange={(o, _) => setMappingEvent(o)}
      value={selectedDestinationMappingEventSelectedOption}
      styles={largeSelectStyles}
      isValidNewOption={(inputValue) => !!inputValue}
      createOptionPosition="last"
      noOptionsMessage={() => noOptionsMsg}
      formatCreateLabel={formatNewLabel}
      getNewOptionData={formatNewOption}
    />
  );
};

export const NewTransformationPageStep2: React.FC<{
  intention: TransformationIntention;
  selectDestination: (destinationID: CustomDestinationOption | null) => void;
  selectedDestination?: DestinationKey | typeof ALL_DESTINATIONS_VALUE | null;
  selectedDestinationDefinition: DestinationDefinition | null;
  selectedDestinationConfig: LoadedDestinationInfo | null;
  selectEventDef: (eventDefID?: unknown) => void;
  selectedDestinationStandardEvent?: DestinationStandardMappingItemNamed | null;
  setSelectedDestinationStandardEvent: (
    standardEvent: DestinationStandardMappingItemNamed | null,
  ) => void;
  selectedEventDef?: string | typeof ALL_EVENTS_VALUE | null;
  selectedEventDefOption?: { value: string; label: string } | null;
  selectedEventDefConfig?: LoadedEventDefinition;
  setPropertyMapping: (properties: Record<string, string>) => void;
  propertyMapping: Record<string, string>;
  refreshEventDefinitions: () => void;
  duplicateTransformationSet: TransformDefinitionSet | null;
  availableDestsIDToKey: Record<string, DestinationKey>;
}> = ({
  intention,
  selectDestination,
  selectEventDef,
  selectedDestination,
  selectedEventDef,
  selectedEventDefOption,
  selectedEventDefConfig,
  setPropertyMapping,
  selectedDestinationStandardEvent,
  setSelectedDestinationStandardEvent,
  selectedDestinationDefinition,
  refreshEventDefinitions,
  duplicateTransformationSet,
  availableDestsIDToKey,
  selectedDestinationConfig,
}) => {
  const history = useHistory();

  // State for managing paginated event options
  const [allLoadedOptions, setAllLoadedOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const lastSearchQueryRef = useRef<string>("");
  const hasMoreRef = useRef(true);

  const enabledDestinationsForEventDef: Partial<
    Record<DestinationKey | typeof ALL_DESTINATIONS_VALUE, boolean>
  > = selectedEventDefConfig
    ? getEnabledDestinationsForEventDef(
        selectedEventDefConfig.enabled_dests,
        availableDestsIDToKey,
      )
    : {};
  const [errorMessage, setErrorMessage] = useState("");
  const [enabling, setEnabling] = useState(false);
  const [selectedStandardMappingEventName, selectStandardMappingEventName] =
    useState<string | null>(null);
  const [
    selectedDestinationStandardMappingConfig,
    setSelectedDestinationStandardMappingConfig,
  ] = useState<DestinationStandardMapping | null>(null);

  const destinationMetaManager = useDestinationMetadataManager();

  const selectedEventDefName = selectedEventDefConfig
    ? selectedEventDefConfig.name
    : null;
  let selectedDestinationMappingEventOptions: Array<{
    value: string;
    label: React.ReactNode;
  }> = [];
  // TODO: fix this any
  let selectedDestinationMappingEventSelectedOption: any = null;

  let hasSelectedAllDestinations =
    selectedDestination === ALL_DESTINATIONS_VALUE;
  let hasSelectedAllEvents = selectedEventDef === ALL_EVENTS_VALUE;

  if (selectedDestination && selectedDestinationDefinition) {
    if (
      selectedEventDefName &&
      selectedDestinationDefinition.type === "advertising" &&
      selectedDestinationDefinition.hasEventMapping &&
      selectedDestinationStandardMappingConfig
    ) {
      selectedDestinationMappingEventOptions = _.map(
        sortedEventMappingForEvent(
          selectedEventDefName,
          selectedDestinationStandardMappingConfig.items,
        ),
        (event, index) => ({
          value: event.eventName,
          label: (
            <>
              <div className="text-md text-textDefault">
                {event.eventName}{" "}
                {index === 0 &&
                  stringDistance(selectedEventDefName, event.eventName) < 8 && (
                    <Tooltip content="This looks like a good match">
                      <Target
                        style={{ position: "relative", top: -2 }}
                        className="text-turquoise-800"
                      />
                    </Tooltip>
                  )}
              </div>
              <div className="text-textDefault text-sm">
                {event.description}
              </div>
            </>
          ),
        }),
      );
      selectedDestinationMappingEventSelectedOption =
        selectedDestinationStandardEvent
          ? _.find(
              selectedDestinationMappingEventOptions,
              (opt) => opt.value === selectedDestinationStandardEvent.eventName,
            )
          : null;
      if (
        selectedDestinationStandardEvent &&
        !selectedDestinationMappingEventSelectedOption &&
        selectedDestinationStandardMappingConfig.wildcard
      ) {
        selectedDestinationMappingEventSelectedOption = {
          value: selectedDestinationStandardEvent.eventName,
          label: (
            <WildcardCustomOption
              inputValue={selectedDestinationStandardEvent.eventName}
              wildcardConfig={selectedDestinationStandardMappingConfig.wildcard}
            />
          ),
        };
      }
    }
  }

  useEffect(() => {
    setErrorMessage("");
  }, [selectedDestination, selectedEventDef, intention]);

  useEffect(() => {
    const update = async () => {
      if (
        selectedDestinationDefinition &&
        selectedDestinationDefinition.type === "advertising" &&
        selectedDestinationDefinition.getStandardMapping
      ) {
        const mapping =
          await selectedDestinationDefinition.getStandardMapping();
        setSelectedDestinationStandardMappingConfig(mapping);
      } else {
        setSelectedDestinationStandardMappingConfig(null);
      }
    };
    update();
  }, [selectedDestinationDefinition]);

  useEffect(() => {
    const mappingDict: Record<string, string> = {};
    if (selectedDestinationStandardEvent) {
      _.forEach(selectedDestinationStandardEvent.propertiesArr, (prop) => {
        mappingDict[prop.key] = prop.key;
      });
    }
    setPropertyMapping(mappingDict);
  }, [selectedDestinationStandardEvent, setPropertyMapping]);

  useEffect(() => {
    if (selectedDestinationStandardMappingConfig) {
      let foundItem = _.find(
        selectedDestinationStandardMappingConfig.items,
        (opt) => opt.eventName === selectedStandardMappingEventName,
      );
      if (
        !foundItem &&
        selectedDestinationStandardMappingConfig.wildcard &&
        selectedStandardMappingEventName
      ) {
        foundItem = {
          propertiesArr:
            selectedDestinationStandardMappingConfig.wildcard.propertiesArr,
          eventName: selectedStandardMappingEventName,
        };
      }
      setSelectedDestinationStandardEvent(foundItem || null);
    } else {
      setSelectedDestinationStandardEvent(null);
    }
  }, [
    selectedDestinationStandardMappingConfig,
    selectedStandardMappingEventName,
    setSelectedDestinationStandardEvent,
  ]);

  const setMappingEvent = (
    option: { label: React.ReactNode; value: string } | null,
  ) => {
    if (option) {
      // @ts-ignore
      selectStandardMappingEventName(option.value);
    } else {
      selectStandardMappingEventName(null);
    }
  };

  const configuredDestinations = useArrayOfDestinations();
  const enabledDestinationLookup: Record<DestinationSlug, boolean> =
    useMemo(() => {
      return configuredDestinations
        .filter((dest) => dest.enabled)
        .reduce((acc, dest) => {
          acc[dest.type_slug] = true;
          return acc;
        }, {});
    }, [configuredDestinations]);

  const makeOption = (
    destinationKey: DestinationKey,
  ): CustomDestinationOption | null => {
    if (!destinationKey) {
      return null;
    }
    const destinationSlug = destinationKeyToSlugMap[destinationKey];
    const dest = allDestinationsData[destinationSlug];
    if (!dest) {
      return null;
    }

    const Icon = dest.logoSquare;

    return {
      value: dest.key,
      icon: <Icon />,
      label: dest.name,
    };
  };

  const isAdvertisingDestinations = (
    destinationKey: DestinationKey,
  ): boolean => {
    const destinationSlug = destinationKeyToSlugMap[destinationKey];
    const dest = allDestinationsData[destinationSlug];
    return !!dest && dest.type === "advertising" && dest.hasEventMapping;
  };

  const destinationIsDisabledForEventDef =
    selectedDestination &&
    (selectedEventDefConfig === undefined ||
      !selectedEventDefConfig.segment_compatible) &&
    !enabledDestinationsForEventDef[selectedDestination];

  let filteredDestinations =
    intention === "standardEvent"
      ? _.filter(availableDestsIDToKey, isAdvertisingDestinations)
      : availableDestsIDToKey;

  // @ts-ignore
  let destinationOptions: CustomDestinationOption[] = _.chain(
    filteredDestinations,
  )
    .filter((e: DestinationKey) => {
      const slug = destinationKeyToSlugMap[e];
      return slug in enabledDestinationLookup && enabledDestinationLookup[slug];
    })
    .filter((e: DestinationKey) => {
      const slug = destinationKeyToSlugMap[e];
      const dest = allDestinationsData[slug];
      return dest && dest.type !== "warehouse" && dest.supportsTrack !== false;
    })
    .map(makeOption)
    .filter((o) => !!o)
    .orderBy("value")
    .uniqBy("label")
    .value();

  if (intention !== "standardEvent") {
    destinationOptions = _.concat([allDestinationOption], destinationOptions);
  }

  const selectedDestinationOption = _.find(
    destinationOptions,
    (opt) => opt.value === selectedDestination,
  );

  // selectedEventDefOption is now passed as a prop from parent

  // Load initial page of events
  const loadInitialEvents = useCallback(async (inputValue: string) => {
    setIsLoading(true);
    lastSearchQueryRef.current = inputValue;
    hasMoreRef.current = true;

    try {
      const response = await makeRequest<{ event_defs: EventDefinition[] }>(
        "get_event_defs",
        {
          page_size: 100,
          search_query: inputValue.trim(),
        },
      );

      const eventDefs = response.data.event_defs || [];
      const options = eventDefs.map((def) => ({
        value: def.id,
        label: def.name,
      }));

      hasMoreRef.current = eventDefs.length === 100;
      setAllLoadedOptions(options);
    } catch (error) {
      console.error("[loadInitialEvents] error:", error);
      setAllLoadedOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle search input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadInitialEvents(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, loadInitialEvents]);

  // Load more events (called from MenuList on scroll)
  const loadMoreEvents = useCallback(async () => {
    if (isLoadingMore || !hasMoreRef.current || allLoadedOptions.length === 0) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const lastOption = allLoadedOptions[allLoadedOptions.length - 1];
      const response = await makeRequest<{ event_defs: EventDefinition[] }>(
        "get_event_defs",
        {
          page_size: 100,
          search_query: lastSearchQueryRef.current.trim(),
          start_after_name: lastOption?.label,
        },
      );

      const eventDefs = response.data.event_defs || [];
      const newOptions = eventDefs.map((def) => ({
        value: def.id,
        label: def.name,
      }));

      if (newOptions.length > 0) {
        setAllLoadedOptions((prev) => [...prev, ...newOptions]);
        hasMoreRef.current = newOptions.length === 100;
      } else {
        hasMoreRef.current = false;
      }
    } catch (error) {
      console.error("[loadMoreEvents] error:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [allLoadedOptions, isLoadingMore]);

  // Combined options (All Events + loaded options)
  const displayOptions: { value: string; label: string }[] = useMemo(() => {
    if (intention !== "standardEvent") {
      return [allEventsOption, ...allLoadedOptions];
    }
    return allLoadedOptions;
  }, [intention, allLoadedOptions]);

  // Handle scroll to bottom - load more events if available
  const handleMenuScrollToBottom = useCallback(() => {
    if (hasMoreRef.current && !isLoadingMore) {
      loadMoreEvents();
    }
  }, [isLoadingMore, loadMoreEvents]);

  const enableDestination = () => {
    if (selectedDestination && selectedEventDef) {
      setEnabling(true);
      makeRequest<any>("toggle_destination_by_name", {
        dest_name: selectedDestination,
        event_def_id: selectedEventDef,
        state: true,
      }).then((resp) => {
        setEnabling(false);
        if (resp.data.error_message) {
          setErrorMessage(resp.data.error_message);
        } else {
          setErrorMessage("");
          refreshEventDefinitions();
        }
      });
    }
  };

  return (
    <div
      className="mt-8 flex flex-col items-center justify-center"
      key={`step-2-${intention}`}
    >
      <div className="flex w-1/3 flex-col justify-between">
        {intention !== "identifyEvent" && (
          <Select
            key={`event-select-${intention}`}
            className="mb-4 w-full"
            placeholder="Search events..."
            options={displayOptions}
            onChange={selectEventDef}
            value={selectedEventDefOption}
            onInputChange={(value) => setSearchInput(value)}
            onMenuScrollToBottom={handleMenuScrollToBottom}
            styles={selectStyles}
            isLoading={isLoading || isLoadingMore}
            filterOption={null}
            noOptionsMessage={() => "No events found"}
          />
        )}
        <DestinationSelect
          className="mb-4 w-full"
          onChange={selectDestination}
          options={destinationOptions}
          value={selectedDestinationOption}
          styles={selectStyles}
        />
        {!hasSelectedAllDestinations &&
          !hasSelectedAllEvents &&
          selectedEventDefConfig &&
          selectedDestinationConfig &&
          destinationIsDisabledForEventDef &&
          selectedDestinationConfig &&
          (destinationMetaManager.destinationIsValidForEvent(
            selectedDestinationConfig,
            eventDefType(selectedEventDefConfig),
          ) ? (
            <div className="text-textError mt-1 text-sm">
              <OctagonAlert style={{ position: "relative", top: -2 }} /> This
              destination isn&apos;t enabled for this event.{" "}
              <span
                className={clsx("underline", {
                  "opacity-50": enabling,
                  "cursor-pointer": !enabling,
                })}
                onClick={enableDestination}
              >
                Enable it?
              </span>
            </div>
          ) : (
            <div className="text-textError mt-1 text-sm">
              <OctagonAlert style={{ position: "relative", top: -2 }} /> This
              event cannot be sent to this destination.
            </div>
          ))}

        {intention === "fixBadData" &&
          hasSelectedAllDestinations &&
          hasSelectedAllEvents && (
            <div className="text-textError mt-1 text-sm">
              <OctagonAlert /> You must choose an event, a destination or both.
            </div>
          )}
        {duplicateTransformationSet && (
          <div className="text-textError mt-1 text-sm">
            <OctagonAlert /> You already have a transformation for this event +
            destination combination.{" "}
            <span
              className="cursor-pointer underline"
              onClick={() =>
                history.push(
                  `/transformations?expanded=${duplicateTransformationSet.id}`,
                )
              }
            >
              View it here.
            </span>
          </div>
        )}
        {!duplicateTransformationSet && (
          <>
            {intention === "standardEvent" &&
              selectedDestinationDefinition &&
              selectedDestinationDefinition.type === "advertising" && (
                <div className="text-textSecondary mt-1 text-lg">
                  {selectedDestinationDefinition.eventMappingBodyHelp}
                </div>
              )}

            {/* Event Mapping Choice */}
            {intention === "standardEvent" &&
              selectedEventDef &&
              selectedDestinationDefinition &&
              selectedDestinationMappingEventOptions &&
              !duplicateTransformationSet &&
              (!!selectedDestinationStandardMappingConfig &&
              selectedDestinationStandardMappingConfig.wildcard ? (
                <SelectStandardEventWithWildcard
                  selectedDestinationDefinition={selectedDestinationDefinition}
                  selectedDestinationMappingEventOptions={
                    selectedDestinationMappingEventOptions
                  }
                  selectedDestinationMappingEventSelectedOption={
                    selectedDestinationMappingEventSelectedOption
                  }
                  selectedDestinationStandardMappingConfig={
                    selectedDestinationStandardMappingConfig
                  }
                  setMappingEvent={setMappingEvent}
                />
              ) : (
                <Select
                  className="mt-4 mb-4 h-6 w-full"
                  // @ts-ignore
                  placeholder={
                    selectedDestinationDefinition instanceof SaaSDestination &&
                    selectedDestinationDefinition.eventMappingEventName
                      ? selectedDestinationDefinition.eventMappingEventName
                      : `${selectedDestinationDefinition.name} Standard Mappings...`
                  }
                  options={selectedDestinationMappingEventOptions}
                  onChange={setMappingEvent}
                  value={selectedDestinationMappingEventSelectedOption}
                  styles={largeSelectStyles}
                  isSearchable={false}
                />
              ))}
          </>
        )}
        {errorMessage && (
          <p className="text-textError text-xs">{errorMessage}</p>
        )}
      </div>
    </div>
  );
};
